import React, { useState, useEffect, useCallback, useRef } from "react";
import { Typography, Layout, Input, Avatar, Badge, Spin, Empty, message, Tag, Tooltip } from "antd";
import { SearchOutlined, SendOutlined, RobotOutlined, BellOutlined } from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment-timezone";

import { WHITE, GRAY_50, GRAY_200, PRIMARY_600 } from "../../constant/colors";
import { getChatConversationsAPI, getChatMessagesAPI, sendChatMessageAPI, markChatAsReadAPI } from "../../api/api";

const { Text } = Typography;
const { Sider, Content } = Layout;

// ─── Styled Components ──────────────────────────────────────────────────────

const PageContainer = styled(Layout)`
  width: 98%;
  margin: 0 auto;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  overflow: hidden;
`;

const SidebarContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${GRAY_200};
`;

const ChatArea = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const MessagesScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: ${GRAY_50};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Bubble = styled.div`
  max-width: 65%;
  padding: 12px 16px;
  border-radius: ${(props) => (props.$isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px")};
  background: ${(props) => (props.$isOwn ? PRIMARY_600 : WHITE)};
  color: ${(props) => (props.$isOwn ? WHITE : "#393735")};
  align-self: ${(props) => (props.$isOwn ? "flex-end" : "flex-start")};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-size: 14px;
  line-height: 1.5;
`;

const TimeStamp = styled.div`
  font-size: 11px;
  color: #8f8c86;
  align-self: ${(props) => (props.$isOwn ? "flex-end" : "flex-start")};
  margin-bottom: 8px;
`;

const InputBar = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${GRAY_200};
  display: flex;
  gap: 10px;
  align-items: center;
  background: ${WHITE};
`;

const POLL_INTERVAL_MESSAGES = 5000;
const POLL_INTERVAL_LIST = 30000;

// ─── Component ──────────────────────────────────────────────────────────────

const CommunicationCenter = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const listPollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await getChatConversationsAPI({ limit: 50 });
      setConversations(data.conversations || []);
    } catch {
      // Silent fail for polling
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const { data } = await getChatMessagesAPI(conversationId);
      setMessages(data);
    } catch {
      // Silent fail for polling
    }
  }, []);

  useEffect(() => {
    setLoadingList(true);
    fetchConversations().finally(() => setLoadingList(false));
    listPollRef.current = setInterval(fetchConversations, POLL_INTERVAL_LIST);
    return () => {
      clearInterval(listPollRef.current);
      clearInterval(pollRef.current);
    };
  }, [fetchConversations]);

  // Auto-select conversation when navigated with ?patientId=xxx
  useEffect(() => {
    if (conversations.length > 0 && !selectedConversation) {
      const params = new URLSearchParams(window.location.search);
      const targetPatientId = params.get("patientId");
      if (targetPatientId) {
        const match = conversations.find((c) => String(c.patientId) === String(targetPatientId));
        if (match) setSelectedConversation(match);
      }
    }
  }, [conversations, selectedConversation]);

  useEffect(() => {
    if (selectedConversation) {
      setLoadingMessages(true);
      fetchMessages(selectedConversation.id).finally(() => setLoadingMessages(false));
      markChatAsReadAPI(selectedConversation.id).catch(() => {});
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(selectedConversation.id), POLL_INTERVAL_MESSAGES);
    }
    return () => clearInterval(pollRef.current);
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    setSending(true);
    try {
      await sendChatMessageAPI(selectedConversation.id, messageInput.trim());
      setMessageInput("");
      fetchMessages(selectedConversation.id);
    } catch {
      message.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getPatientName = (conv) => {
    const patient = conv.patient;
    if (!patient) return "Unknown Patient";
    return patient.firstName
      ? `${patient.firstName} ${patient.lastName || ""}`
      : `Patient #${patient.id || conv.patient}`;
  };

  const getPatientMRN = (conv) => {
    // mrn may be a numeric FK or an actual MRN string
    const mrn = conv.patient?.mrn;
    if (!mrn) return "";
    return `MRN-${mrn}`;
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery.trim()) return true;
    const name = getPatientName(conv).toLowerCase();
    const mrn = getPatientMRN(conv).toLowerCase();
    const q = searchQuery.toLowerCase();
    return name.includes(q) || mrn.includes(q);
  });

  // Group messages by date for "TODAY" / date separators
  const renderMessages = () => {
    if (messages.length === 0) {
      return <Empty description="No messages yet" style={{ marginTop: 40 }} />;
    }

    let lastDate = "";
    return messages.map((msg) => {
      const msgDate = moment(msg.createdAt).format("YYYY-MM-DD");
      const isOwn = msg.senderType === "careteam";
      let dateSeparator = null;

      if (msgDate !== lastDate) {
        lastDate = msgDate;
        const isToday = moment(msg.createdAt).isSame(moment(), "day");
        const isYesterday = moment(msg.createdAt).isSame(moment().subtract(1, "day"), "day");
        const label = isToday ? "TODAY" : isYesterday ? "YESTERDAY" : moment(msg.createdAt).format("MMM D, YYYY");
        dateSeparator = (
          <div
            key={`sep-${msgDate}`}
            style={{
              textAlign: "center",
              margin: "16px 0 8px",
              fontSize: 11,
              color: "#8f8c86",
              fontWeight: 700,
              letterSpacing: 0.5
            }}
          >
            {label}
          </div>
        );
      }

      const readLabel = isOwn && msg.readAt ? " • Read" : "";

      return (
        <React.Fragment key={msg.id}>
          {dateSeparator}
          <Bubble $isOwn={isOwn}>
            {msg.isAiGenerated && (
              <div style={{ marginBottom: 6 }}>
                <Tooltip title="This message was generated by AI assistant Sarah">
                  <Tag
                    icon={<RobotOutlined />}
                    color="cyan"
                    style={{ borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: "help" }}
                  >
                    AI Response
                  </Tag>
                </Tooltip>
              </div>
            )}
            {msg.senderId?.startsWith("nudge-engine") &&
              (() => {
                const eventType = msg.senderId.split(":")[1] || "";
                const eventLabels = {
                  pa_status_changed: "PA Status Changed",
                  insurance_changed: "Insurance Changed",
                  pa_expiration: "PA Expiration",
                  journey_step_completed: "Journey Step Completed",
                  enrollment_completed: "Enrollment Completed",
                  shipment_at_risk: "Shipment at Risk",
                  copay_card_expiring: "Copay Card Expiring",
                  symptom_reported: "Symptom Reported",
                  clinical_milestone: "Clinical Milestone",
                  coverage_change_risk: "Coverage Change Risk",
                  custom: "Custom Event"
                };
                const label = eventLabels[eventType] || eventType.replace(/_/g, " ");
                return (
                  <div style={{ marginBottom: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <Tag
                      icon={<BellOutlined />}
                      color="orange"
                      style={{ borderRadius: 10, fontSize: 11, fontWeight: 700 }}
                    >
                      Automated Nudge
                    </Tag>
                    {label && (
                      <Tag color="default" style={{ borderRadius: 10, fontSize: 11 }}>
                        {label}
                      </Tag>
                    )}
                  </div>
                );
              })()}
            {msg.content}
          </Bubble>
          <TimeStamp $isOwn={isOwn}>
            {moment(msg.createdAt).format("hh:mm A")}
            {msg.isAiGenerated ? " • AI" : msg.senderId?.startsWith("nudge-engine") ? " • Nudge" : ""}
            {readLabel}
          </TimeStamp>
        </React.Fragment>
      );
    });
  };

  return (
    <PageContainer>
      {/* ─── Left sidebar: conversation list ─────────────────────────────── */}
      <Sider width={340} style={{ background: WHITE, height: "100%" }}>
        <SidebarContainer>
          <div style={{ padding: "20px 16px 12px" }}>
            <Text strong style={{ fontSize: 20, display: "block" }}>
              Communication Center
            </Text>
            <Text style={{ fontSize: 13, color: "#8f8c86" }}>{conversations.length} conversations</Text>
          </div>

          <div style={{ padding: "0 16px 12px" }}>
            <Input
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="Search patients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>

          {loadingList ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spin />
            </div>
          ) : (
            <div style={{ overflow: "auto", flex: 1 }}>
              {filteredConversations.map((conv) => {
                const isSelected = selectedConversation?.id === conv.id;
                const name = getPatientName(conv);
                const mrn = getPatientMRN(conv);
                const lastMsg = conv.lastMessagePreview || (conv.lastMessageAt ? "..." : "No messages");
                const timeLabel = conv.lastMessageAt ? moment(conv.lastMessageAt).fromNow(true) + " ago" : "";

                return (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    style={{
                      padding: "12px 16px",
                      cursor: "pointer",
                      background: isSelected ? "#EBF5FF" : WHITE,
                      borderLeft: isSelected ? `3px solid ${PRIMARY_600}` : "3px solid transparent",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start"
                    }}
                  >
                    <Avatar
                      size={36}
                      style={{ background: isSelected ? PRIMARY_600 : "#d9d9d9", flexShrink: 0, fontSize: 14 }}
                    >
                      {name[0]}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Text strong style={{ fontSize: 13, color: "#393735" }}>
                          {name}
                          {mrn ? ` • ${mrn}` : ""}
                        </Text>
                        <Text style={{ fontSize: 11, color: "#8f8c86", flexShrink: 0 }}>{timeLabel}</Text>
                      </div>
                      <div
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}
                      >
                        <Text
                          style={{
                            fontSize: 12,
                            color: "#8f8c86",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1
                          }}
                        >
                          {conv.lastMessageIsAi ? "🤖 " : ""}
                          {lastMsg}
                        </Text>
                        {conv.unreadCountCareTeam > 0 && (
                          <Badge count={conv.unreadCountCareTeam} size="small" style={{ marginLeft: 6 }} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SidebarContainer>
      </Sider>

      {/* ─── Right: chat area ────────────────────────────────────────────── */}
      <Content>
        <ChatArea>
          {selectedConversation ? (
            <>
              {/* Patient header */}
              <div
                style={{
                  padding: "12px 20px",
                  borderBottom: `1px solid ${GRAY_200}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar size={40} style={{ background: PRIMARY_600 }}>
                    {getPatientName(selectedConversation)[0]}
                  </Avatar>
                  <div>
                    <Text strong style={{ fontSize: 15 }}>
                      {getPatientName(selectedConversation)}
                      {getPatientMRN(selectedConversation) ? ` • ${getPatientMRN(selectedConversation)}` : ""}
                    </Text>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: "#52c41a",
                          display: "inline-block"
                        }}
                      />
                      <Text style={{ fontSize: 12, color: "#52c41a" }}>Online</Text>
                      <Text style={{ fontSize: 12, color: "#8f8c86" }}>•</Text>
                      <Text
                        style={{ fontSize: 12, color: PRIMARY_600, cursor: "pointer" }}
                        onClick={() => {
                          const pid = selectedConversation.patientId || selectedConversation.patient?.id;
                          if (pid) window.location.href = `/patient/${pid}`;
                        }}
                      >
                        View Profile
                      </Text>
                    </div>
                  </div>
                </div>
                <Tag color="green" style={{ borderRadius: 12, fontWeight: 700, fontSize: 12 }}>
                  ENROLLED
                </Tag>
              </div>

              {/* Messages */}
              <MessagesScrollArea>
                {loadingMessages ? (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <Spin />
                  </div>
                ) : (
                  renderMessages()
                )}
                <div ref={messagesEndRef} />
              </MessagesScrollArea>

              {/* Input */}
              <InputBar>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "1px solid #d9d9d9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    flexShrink: 0,
                    fontSize: 16
                  }}
                  title="Attach file"
                >
                  +
                </div>
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onPressEnter={handleSend}
                  placeholder="Type your response..."
                  disabled={sending}
                  style={{ borderRadius: 20, height: 40 }}
                />
                <div
                  onClick={handleSend}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: messageInput.trim() ? PRIMARY_600 : "#d9d9d9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: messageInput.trim() ? "pointer" : "default",
                    flexShrink: 0
                  }}
                >
                  <SendOutlined style={{ color: WHITE, fontSize: 16 }} />
                </div>
              </InputBar>

              {/* Footer */}
              <div style={{ textAlign: "center", padding: "6px 0", fontSize: 11, color: "#bfbfbf" }}>
                Messages are encrypted and HIPAA compliant.
              </div>
            </>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                gap: 12,
                color: "#bfbfbf"
              }}
            >
              <div style={{ fontSize: 48 }}>💬</div>
              <Text style={{ fontSize: 15, color: "#8f8c86" }}>Select a conversation to start chatting</Text>
            </div>
          )}
        </ChatArea>
      </Content>
    </PageContainer>
  );
};

export { CommunicationCenter };
